import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { PendingAttributeValue } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { PendingValuesList, TypesTable } from './components';
import {
    type AttributeDictionaryTab,
    type AttributeTypeRow,
} from './components/attributesMeta';

type AttributesPageViewProps = {
    onAddType: () => void;
    onDeleteType: (id: string) => void;
    onEditType: (id: string) => void;
    onManageValues: (id: string) => void;
    onPromoteValue: (typeId: string, valueId: string) => void;
    onRetryPending: () => void;
    onSearchChange: (value: string) => void;
    onTabChange: (tab: AttributeDictionaryTab) => void;
    pending: PendingAttributeValue[] | null;
    pendingError: unknown;
    search: string;
    tab: AttributeDictionaryTab;
    types: AttributeTypeRow[];
};

const TABS: AttributeDictionaryTab[] = ['types', 'pending'];

export const AttributesPageView: React.FC<AttributesPageViewProps> = ({
    onAddType,
    onDeleteType,
    onEditType,
    onManageValues,
    onPromoteValue,
    onRetryPending,
    onSearchChange,
    onTabChange,
    pending,
    pendingError,
    search,
    tab,
    types,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tabs: {
                types: t('attributes.components.dictionary.tabs.types'),
                pending: t('attributes.components.dictionary.tabs.pending'),
            },
            searchPlaceholder: t(
                'attributes.components.dictionary.searchPlaceholder',
            ),
            searchAria: t('attributes.components.dictionary.searchAria'),
            addType: t('attributes.components.dictionary.addType'),
            tabsAria: t('attributes.components.dictionary.tabsAria'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tabsAria}
            {...getNodeTestIdProp('attribute-dictionary-section')}
        >
            <div
                className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('attribute-dictionary-toolbar')}
            >
                <Segment
                    aria-label={copy.tabsAria}
                    onSelectionChange={(key) =>
                        onTabChange(key as AttributeDictionaryTab)
                    }
                    selectedKey={tab}
                    {...getNodeTestIdProp('attribute-dictionary-tabs')}
                >
                    {TABS.map((value) => (
                        <Segment.Item
                            id={value}
                            key={value}
                            {...getNodeTestIdProp(
                                `attribute-dictionary-tab-${value}`,
                            )}
                        >
                            <Segment.Separator />
                            {copy.tabs[value]}
                        </Segment.Item>
                    ))}
                </Segment>
                {tab === 'types' ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <label
                            className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto"
                            htmlFor="attribute-dictionary-search"
                            {...getNodeTestIdProp(
                                'attribute-dictionary-search-wrap',
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-4 text-muted"
                                icon="gravity-ui:magnifier"
                            />
                            <input
                                aria-label={copy.searchAria}
                                className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                                id="attribute-dictionary-search"
                                onChange={(event) =>
                                    onSearchChange(event.target.value)
                                }
                                placeholder={copy.searchPlaceholder}
                                type="text"
                                value={search}
                                {...getNodeTestIdProp(
                                    'attribute-dictionary-search',
                                )}
                            />
                        </label>
                        <Button
                            onPress={onAddType}
                            variant="primary"
                            {...getNodeTestIdProp('attribute-dictionary-add')}
                        >
                            <Icon icon="gravity-ui:plus" />
                            {copy.addType}
                        </Button>
                    </div>
                ) : null}
            </div>
            {tab === 'types' ? (
                <TypesTable
                    data={types}
                    onDeleteType={onDeleteType}
                    onEditType={onEditType}
                    onManageValues={onManageValues}
                />
            ) : (
                <PendingValuesList
                    error={pendingError}
                    items={pending}
                    onPromote={onPromoteValue}
                    onRetry={onRetryPending}
                />
            )}
        </section>
    );
};
